import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Send, UserPlus, Trash2, CheckCircle, Search, RefreshCw, Bot, Shield, ChefHat, BarChart3, Copy, UserCheck, UserX, Clock, Sun, Moon, Mail, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';

const BOT_INFO = {
  factures: { label: 'LemasFactures', icon: Shield, desc: 'Alertes prix & factures', color: 'text-orange-400', bgColor: 'bg-orange-400/10' },
  chef: { label: 'LemasChef', icon: ChefHat, desc: 'Budget & scans chef', color: 'text-emerald-400', bgColor: 'bg-emerald-400/10' },
  ca: { label: 'LemasCA', icon: BarChart3, desc: 'Résumé CA & rapports', color: 'text-blue-400', bgColor: 'bg-blue-400/10' },
};

export default function PatronTelegram() {
  const [configs, setConfigs] = useState([]);
  const [bots, setBots] = useState([]);
  const [scanning, setScanning] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastScanResult, setLastScanResult] = useState(null);
  const [emailTheme, setEmailTheme] = useState('dark');
  const [sendingTest, setSendingTest] = useState('');
  const [sendingMax, setSendingMax] = useState(false);

  useEffect(() => { fetchAll(); fetchEmailTheme(); }, []);

  const fetchEmailTheme = async () => {
    try {
      const res = await api.get('/settings/email-theme');
      setEmailTheme(res.data.theme);
    } catch {}
  };

  const toggleEmailTheme = async () => {
    const newTheme = emailTheme === 'dark' ? 'light' : 'dark';
    try {
      await api.post('/settings/email-theme', { theme: newTheme });
      setEmailTheme(newTheme);
      toast.success(`Theme email: ${newTheme === 'dark' ? 'Sombre' : 'Clair'}`);
    } catch { toast.error('Erreur'); }
  };

  const sendTestEmail = async (type) => {
    setSendingTest(type);
    try {
      if (type === 'weekly') {
        await api.post('/email/weekly-report');
        toast.success('Email hebdo envoye');
      } else {
        await api.post('/export/bilan-pdf-email/2026');
        toast.success('Email bilan envoye');
      }
    } catch { toast.error('Erreur envoi'); }
    setSendingTest('');
  };

  const sendMaxPriorities = async () => {
    setSendingMax(true);
    try {
      await api.post('/telegram/max-priorities');
      toast.success('Priorites Max envoyees sur Telegram');
    } catch { toast.error('Erreur envoi Max'); }
    setSendingMax(false);
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, botRes] = await Promise.all([
        api.get('/telegram/configs'),
        api.get('/telegram/bots-status')
      ]);
      setConfigs(cfgRes.data);
      setBots(botRes.data.bots || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  const scanAndRegister = async (botType) => {
    setScanning(botType);
    try {
      const res = await api.get(`/telegram/scan-contacts/${botType}?auto_register=true`);
      const data = res.data;
      setLastScanResult(data);
      
      if (data.newly_pending && data.newly_pending.length > 0) {
        toast.success(`${data.newly_pending.join(', ')} en attente de validation`);
        fetchAll();
      } else {
        const unregistered = (data.contacts || []).filter(c => !c.already_registered);
        if (unregistered.length === 0 && data.contacts.length > 0) {
          toast.info('Tous les contacts sont déjà inscrits');
        } else if (data.contacts.length === 0) {
          toast.info('Aucun message récent trouvé — demandez à la personne d\'envoyer un message au bot');
        }
      }
    } catch { toast.error('Erreur scan — vérifiez que le bot est configuré'); }
    setScanning('');
  };

  const scanAllBots = async () => {
    setScanning('all');
    let totalNew = 0;
    for (const bot of bots) {
      if (bot.configured) {
        try {
          const res = await api.get(`/telegram/scan-contacts/${bot.type}?auto_register=true`);
          totalNew += (res.data.newly_pending || []).length;
        } catch { /* continue */ }
      }
    }
    if (totalNew > 0) {
      toast.success(`${totalNew} nouvelle(s) demande(s) en attente`);
    } else {
      toast.info('Aucun nouveau contact détecté');
    }
    fetchAll();
    setScanning('');
  };

  const approveContact = async (chatId, nom) => {
    try {
      await api.post(`/telegram/approve/${chatId}`);
      toast.success(`${nom} approuvé — message envoyé sur Telegram`);
      fetchAll();
    } catch { toast.error('Erreur approbation'); }
  };

  const rejectContact = async (chatId) => {
    try {
      await api.post(`/telegram/reject/${chatId}`);
      toast.success('Demande refusée');
      fetchAll();
    } catch { toast.error('Erreur refus'); }
  };

  const deleteConfig = async (chatId) => {
    try {
      await api.delete(`/telegram/configs/${chatId}`);
      toast.success('Supprimé');
      fetchAll();
    } catch { toast.error('Erreur suppression'); }
  };

  const testConfig = async (chatId) => {
    try {
      await api.post(`/telegram/test/${chatId}`);
      toast.success('Message test envoyé sur Telegram');
    } catch { toast.error('Erreur envoi test'); }
  };

  const changeRole = async (cfg) => {
    const newRole = cfg.role === 'patron' ? 'chef' : 'patron';
    try {
      await api.post('/telegram/register', { chat_id: cfg.chat_id, nom: cfg.nom, role: newRole });
      toast.success(`${cfg.nom} est maintenant ${newRole}`);
      fetchAll();
    } catch { toast.error('Erreur modification'); }
  };

  if (loading) return <div className="text-center py-12 text-[rgb(var(--lm-text-secondary))]">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="telegram-page">
      {/* How it works */}
      <Card className="bg-[rgb(var(--lm-surface))] border-lemas-gold/30">
        <CardContent className="pt-5">
          <h3 className="text-base font-semibold text-lemas-gold mb-3">Comment ajouter quelqu'un ?</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-lemas-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-lemas-gold text-sm font-bold">1</span>
              </div>
              <div>
                <p className="text-sm text-[rgb(var(--lm-text))] font-medium">La personne ouvre le bot</p>
                <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-0.5">Sur Telegram, elle cherche le bot et envoie <b>n'importe quel message</b></p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-lemas-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-lemas-gold text-sm font-bold">2</span>
              </div>
              <div>
                <p className="text-sm text-[rgb(var(--lm-text))] font-medium">Vous cliquez "Scanner"</p>
                <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-0.5">Ici, sur le bot concerné — le contact apparaît</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-full bg-lemas-gold/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-lemas-gold text-sm font-bold">3</span>
              </div>
              <div>
                <p className="text-sm text-[rgb(var(--lm-text))] font-medium">Vous validez la demande</p>
                <p className="text-xs text-[rgb(var(--lm-text-secondary))] mt-0.5">Ici, vous acceptez ou refusez — seuls les approuvés reçoivent les alertes</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bots with scan */}
      <Card className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-[rgb(var(--lm-text))] flex items-center gap-2">
              <Bot size={18} className="text-lemas-gold" /> Bots Telegram
            </CardTitle>
            <Button
              onClick={scanAllBots} size="sm"
              className="bg-lemas-gold/20 text-lemas-gold hover:bg-lemas-gold/30 text-xs"
              disabled={!!scanning}
              data-testid="scan-all-btn"
            >
              {scanning === 'all' ? <RefreshCw size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />}
              Scanner tous
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {bots.map(bot => {
            const info = BOT_INFO[bot.type] || {};
            const Icon = info.icon || Bot;
            const botConfigs = configs.filter(c => {
              if (bot.type === 'chef') return c.role === 'chef';
              return c.role === 'patron';
            });
            return (
              <div key={bot.type} className={`rounded-lg p-4 ${info.bgColor} border border-[rgb(var(--lm-border))]`} data-testid={`bot-${bot.type}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon size={18} className={info.color} />
                    <div>
                      <p className="text-sm font-semibold text-[rgb(var(--lm-text))]">@{info.label}</p>
                      <p className="text-xs text-[rgb(var(--lm-text-secondary))]">{info.desc}</p>
                    </div>
                    {bot.configured && <CheckCircle size={14} className="text-emerald-400" />}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    className={`text-xs ${info.color} border-current hover:bg-[rgb(var(--lm-surface-elevated))]`}
                    onClick={() => scanAndRegister(bot.type)}
                    disabled={!!scanning}
                    data-testid={`scan-${bot.type}`}
                  >
                    {scanning === bot.type ? <RefreshCw size={12} className="animate-spin mr-1" /> : <Search size={12} className="mr-1" />}
                    Scanner & inscrire
                  </Button>
                </div>
                {botConfigs.length > 0 && (
                  <div className="mt-2 pl-6 text-xs text-[rgb(var(--lm-text-secondary))]">
                    Reçoit les alertes : {botConfigs.map(c => c.nom).join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Pending approvals */}
      {configs.filter(c => c.status === 'pending').length > 0 && (
        <Card className="bg-[rgb(var(--lm-surface))] border-orange-400/30" data-testid="pending-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-orange-400 flex items-center gap-2">
              <Clock size={18} /> En attente de validation ({configs.filter(c => c.status === 'pending').length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {configs.filter(c => c.status === 'pending').map((cfg, i) => (
              <div key={i} className="flex items-center justify-between bg-orange-400/5 border border-orange-400/20 rounded-lg px-4 py-3" data-testid={`pending-${cfg.chat_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                  <div>
                    <p className="text-sm text-[rgb(var(--lm-text))] font-medium">{cfg.nom}</p>
                    <p className="text-xs text-[rgb(var(--lm-text-secondary))]">
                      {cfg.role === 'patron' ? 'Patron' : 'Chef'} · Bot: {cfg.bot_type || '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => approveContact(cfg.chat_id, cfg.nom)}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
                    data-testid={`approve-${cfg.chat_id}`}>
                    <UserCheck size={14} className="mr-1" /> Accepter
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => rejectContact(cfg.chat_id)}
                    className="text-red-400 hover:bg-red-400/10 text-xs"
                    data-testid={`reject-${cfg.chat_id}`}>
                    <UserX size={14} className="mr-1" /> Refuser
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Registered users */}
      <Card className="bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-[rgb(var(--lm-text))] flex items-center gap-2">
              <Send size={18} className="text-lemas-gold" /> Personnes approuvées ({configs.filter(c => c.status !== 'pending').length})
            </CardTitle>
            <Button onClick={fetchAll} size="sm" variant="ghost" className="text-[rgb(var(--lm-text-secondary))]">
              <RefreshCw size={14} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {configs.filter(c => c.status !== 'pending').length === 0 ? (
            <p className="text-sm text-[rgb(var(--lm-text-secondary))] text-center py-4">Aucune personne approuvée</p>
          ) : (
            <div className="space-y-2">
              {configs.filter(c => c.status !== 'pending').map((cfg, i) => (
                <div key={i} className="flex items-center justify-between bg-lemas-bg/50 rounded-lg px-4 py-3" data-testid={`config-${cfg.chat_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${cfg.alerts_enabled ? 'bg-emerald-400' : 'bg-red-400'}`} />
                    <div>
                      <p className="text-sm text-[rgb(var(--lm-text))] font-medium">{cfg.nom}</p>
                      <p className="text-xs text-[rgb(var(--lm-text-secondary))]">ID: {cfg.chat_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => changeRole(cfg)}
                      className={`text-xs px-2 py-1 rounded ${cfg.role === 'patron' ? 'bg-lemas-gold/20 text-lemas-gold' : 'bg-emerald-400/20 text-emerald-400'}`}
                      data-testid={`role-${cfg.chat_id}`}
                    >
                      {cfg.role === 'patron' ? 'Patron' : 'Chef'}
                    </button>
                    <Button size="sm" variant="ghost" className="text-xs text-blue-400 hover:bg-blue-400/10"
                      onClick={() => testConfig(cfg.chat_id)} data-testid={`test-${cfg.chat_id}`}>
                      Tester
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs text-red-400 hover:bg-red-400/10"
                      onClick={() => deleteConfig(cfg.chat_id)} data-testid={`delete-${cfg.chat_id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Max IA Daily Priorities */}
      <Card className="bg-[rgb(var(--lm-surface))] border-lemas-gold/20" data-testid="max-priorities-section">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Sparkles size={16} className="text-lemas-gold" /> Max — Priorites quotidiennes
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-2">
          <p className="text-xs text-[rgb(var(--lm-text-secondary))] mb-3">
            Chaque matin a 7h30, Max envoie les 3 priorites du jour au Patron (bot CA) et au Chef (bot Chef) via Telegram.
          </p>
          <Button
            onClick={sendMaxPriorities}
            disabled={sendingMax}
            className="w-full bg-lemas-gold/15 text-lemas-gold hover:bg-lemas-gold/25 text-xs"
            data-testid="send-max-priorities-btn"
          >
            {sendingMax ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2" />}
            Envoyer les priorites Max maintenant
          </Button>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-[rgb(var(--lm-surface))]" data-testid="email-settings">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail size={16} className="text-lemas-gold" /> Emails
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-2 space-y-4">
          {/* Theme toggle */}
          <div className="flex items-center justify-between p-3 bg-lemas-bg/50 rounded-lg">
            <div>
              <p className="text-sm text-[rgb(var(--lm-text))]">Theme des emails</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Rapports hebdo et bilan</p>
            </div>
            <button onClick={toggleEmailTheme}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                emailTheme === 'dark'
                  ? 'bg-[#0D1716] border border-lemas-gold/30 text-lemas-gold'
                  : 'bg-[#F7F5F0] border border-[#B8942F]/30 text-[#1A2F2B]'
              }`} data-testid="toggle-email-theme">
              {emailTheme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
              <span className="text-xs font-medium">{emailTheme === 'dark' ? 'Sombre' : 'Clair'}</span>
            </button>
          </div>

          {/* Test buttons */}
          <div className="flex gap-2">
            <Button onClick={() => sendTestEmail('weekly')} disabled={sendingTest === 'weekly'}
              className="flex-1 bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))] text-xs"
              data-testid="test-email-weekly">
              {sendingTest === 'weekly' ? <RefreshCw size={12} className="mr-2 animate-spin" /> : <Mail size={12} className="mr-2" />}
              Tester rapport hebdo
            </Button>
            <Button onClick={() => sendTestEmail('bilan')} disabled={sendingTest === 'bilan'}
              className="flex-1 bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-secondary))] hover:bg-[rgb(var(--lm-surface-elevated))] text-xs"
              data-testid="test-email-bilan">
              {sendingTest === 'bilan' ? <RefreshCw size={12} className="mr-2 animate-spin" /> : <Mail size={12} className="mr-2" />}
              Tester bilan PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
